import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, Circle, Storefront, ShoppingBagOpen, Wallet, Headset,
  ArrowRight, Sparkle, ArrowSquareOut,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { renderRich } from "../lib/richText";

const CALENDLY_URL = "https://calendly.com/office-tee/30min";

/**
 * Persistent setup checklist — mostrata in cima alla dashboard cliente finché
 * tutti i 3 task non sono completati.
 *   Task 1 (Myze):    check manuale, persistente su user.setup_checks.myze
 *   Task 2 (Shopify): check manuale, persistente su user.setup_checks.shopify
 *   Task 3 (Wallet):  auto → balance > 0
 * Include link Calendly come ultima opzione di supporto.
 */
export default function SetupChecklist({ balance = 0 }) {
  const { user, reload } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);

  const checks = user?.setup_checks || {};
  const myzeDone = Boolean(checks.myze);
  const shopifyDone = Boolean(checks.shopify);
  const walletDone = (balance || 0) > 0;

  const doneCount = (myzeDone ? 1 : 0) + (shopifyDone ? 1 : 0) + (walletDone ? 1 : 0);
  const allDone = doneCount === 3;

  const progress = useMemo(() => Math.round((doneCount / 3) * 100), [doneCount]);

  if (allDone) return null;

  const toggle = async (task, current) => {
    setBusy(task);
    try {
      await api.post("/client/setup/toggle", { task, done: !current });
      await reload?.();
    } catch {
      /* silent */
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="tf-card p-6 lg:p-7 border-2 border-[#CCF22C] relative overflow-hidden"
      data-testid="setup-checklist"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#CCF22C]/20" aria-hidden="true" />
      <div className="relative">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#5A6E63]">
              <Sparkle size={12} weight="fill" className="text-[#D4A017]" /> {t("sc_eyebrow")}
            </div>
            <h2 className="font-display font-black text-2xl mt-1.5 tracking-tight text-[#193529]">
              {t("sc_title")}
            </h2>
            <p className="text-xs text-[#5A6E63] mt-1">
              {t("sc_subtitle")}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#5A6E63]">{t("sc_completed_label")}</div>
            <div className="font-display font-black text-2xl text-[#193529] tf-numeric">
              {doneCount}<span className="text-[#5A6E63] text-lg">/3</span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1.5 rounded-full bg-[#F5F2E8] overflow-hidden">
          <div
            className="h-full bg-[#CCF22C] transition-all duration-500 rounded-full"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>

        {/* Tasks */}
        <ul className="mt-5 space-y-2.5">
          <TaskRow
            testId="task-myze"
            done={myzeDone}
            busy={busy === "myze"}
            onToggle={() => toggle("myze", myzeDone)}
            icon={<Storefront size={18} weight="duotone" />}
            title={t("sc_task_myze_title")}
            desc={t("sc_task_myze_desc")}
          />
          <TaskRow
            testId="task-shopify"
            done={shopifyDone}
            busy={busy === "shopify"}
            onToggle={() => toggle("shopify", shopifyDone)}
            icon={<ShoppingBagOpen size={18} weight="duotone" />}
            title={t("sc_task_shopify_title")}
            desc={t("sc_task_shopify_desc")}
          />
          <TaskRow
            testId="task-wallet"
            done={walletDone}
            auto
            icon={<Wallet size={18} weight="duotone" />}
            title={t("sc_task_wallet_title")}
            desc={walletDone ? t("sc_task_wallet_desc_done") : t("sc_task_wallet_desc_todo")}
            action={!walletDone && (
              <Link to="/client/recharge" className="tf-btn tf-btn-primary !py-2 !px-4 text-xs" data-testid="task-wallet-cta">
                {t("sc_task_wallet_cta")} <ArrowRight size={12} weight="bold" />
              </Link>
            )}
          />
        </ul>

        {/* Calendly help footer */}
        <div className="mt-5 pt-4 border-t border-[#E0DFD4] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-[#5A6E63]">
            <Headset size={16} weight="duotone" className="text-[#193529]" />
            <span>{renderRich(t("sc_help_text"))}</span>
          </div>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tf-btn tf-btn-ghost !py-2 !px-4 text-xs"
            data-testid="task-calendly-link"
          >
            {t("sc_help_cta")} <ArrowSquareOut size={12} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  );
}

function TaskRow({ testId, done, auto, busy, onToggle, icon, title, desc, action }) {
  const { t } = useI18n();
  const clickable = !auto && !busy;
  return (
    <li
      className={`flex items-start gap-3 rounded-2xl border p-3.5 transition-all ${
        done
          ? "bg-[#F5FBE8] border-[#CCF22C]"
          : "bg-white border-[#E0DFD4] hover:border-[#193529]/30"
      }`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={clickable ? onToggle : undefined}
        disabled={!clickable}
        className={`mt-0.5 flex-shrink-0 rounded-full transition-transform ${clickable ? "hover:scale-110 cursor-pointer" : ""} ${
          auto ? "cursor-default" : ""
        } focus:outline-none focus:ring-2 focus:ring-[#193529]/30`}
        aria-label={done ? t("sc_check_done_aria") : t("sc_check_todo_aria")}
        data-testid={`${testId}-check`}
      >
        {done ? (
          <CheckCircle size={24} weight="fill" className="text-[#4A8A3E]" />
        ) : (
          <Circle size={24} weight="regular" className="text-[#C8C6BC]" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[#193529]">{icon}</span>
          <div className={`font-display font-bold text-sm ${done ? "text-[#193529]" : "text-[#193529]"}`}>
            {title}
          </div>
          {auto && (
            <span className="text-[9px] uppercase tracking-widest text-[#5A6E63] bg-[#F5F2E8] px-2 py-0.5 rounded-full">
              {t("sc_auto_badge")}
            </span>
          )}
        </div>
        <p className={`text-xs mt-1 leading-relaxed ${done ? "text-[#5A6E63]" : "text-[#5A6E63]"}`}>
          {desc}
        </p>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </li>
  );
}
